import React, { Fragment, useEffect } from "react"
import { compose } from "redux"
import { connect } from "react-redux"
import { createStructuredSelector } from "reselect"
import PropTypes from "prop-types"

import injectSaga from "utils/injectSaga"
import injectReducer from "utils/injectReducer"

import reducer from "./reducer"
import saga from "./saga"
import {
  makeSelectDimensionsData,
  makeSelectIsLoading,
  makeSelectIsLoadingDimensionsData,
  makeSelectMeasureEngagement,
} from "./selectors"
import { getDimensionsDataWatcher, getMeasureEngagementWatcher } from "./actions"

import "../../../Reports/components/EngagementMeasurement/index.less"

import SummaryWidgets from "../SummaryWidgets"
import WidgetQualitativeQuestion from "../WidgetQualitativeQuestion"
import WidgetQualitativeBubbles from "../WidgetQualitativeBubbles"
import WidgetAnalysisSegment from "../WidgetAnalysisSegment"
import WidgetResultByDimensionEngagement from "../WidgetResultByDimensionEngagement"
import WidgetResultByDivisionEngagement from "../WidgetResultByDivisionEngagement"
import WidgetMultipleChoiceQuestions from "../WidgetMultipleChoiceQuestions"
import SummaryQuestions from "../SummaryQuestions"
import WidgetResume from "../WidgetResume"
import {
  LOCAL_CHECKBOX_EXCLUDE,
  LOCAL_ISOLATED_METRICS,
  isAmbassadorActive,
} from "../../../utils/storage"
import { validate } from "../../../utils/functions"
import { get } from "lodash"
import { verifyFeatureFlag } from "../../../utils/feature-flags"

export function EngagementMeasurement(props) {
  // FIX: Incluir selectedSegment en las dependencias del useEffect
  useEffect(() => {
    const {
      idMeasure,
      getDimensionsData,
      getMeasureEngagement,
      idDivisionSelected,
      idDivisionParent,
      selectedSegment,
      measureGeneral,
    } = props

    if (!validate.exist(idMeasure) || !validate.exist(measureGeneral)) return

    getDimensionsData({
      isHome: true,
      id_measure: idMeasure,
      id_survey_type: measureGeneral.id_survey_type,
      id_segment: selectedSegment,
      isExclude:
        LOCAL_CHECKBOX_EXCLUDE() === "true" &&
        LOCAL_ISOLATED_METRICS() === "true",
      params: {
        id_measure: idMeasure,
        id_division: idDivisionSelected || idDivisionParent,
        id_segment: selectedSegment,
      },
    })

    getMeasureEngagement({
      isHome: true,
      id_measure: idMeasure,
      params: {
        id_measure: idMeasure,
        id_division: idDivisionSelected || idDivisionParent,
        id_segment: selectedSegment,
      },
    })
    // measureGeneral por id (no por objeto): cada getStatus trae un objeto nuevo con los mismos
    // datos y volvía a pedir dimensions/engagement (doble carga, VLENG-12055)
  }, [props.idMeasure, props.idDivisionSelected, props.selectedSegment, props.measureGeneral?.id_measure])

  const renderResultByDimension = () => {
    const {
      typeMeasurement,
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      selectedSegment,
      history,
      dimensionsData,
      isLoadingDimensionsData,
    } = props

    if (!validate.exist(idMeasure)) return null
    if (isLoadingDimensionsData || !dimensionsData || !dimensionsData.data) {
      return null
    }

    return (
      <WidgetResultByDimensionEngagement
        typeMeasurement={typeMeasurement}
        idMeasure={idMeasure}
        measureTotal={measureTotal}
        history={history}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
        dimensions={dimensionsData}
      />
    )
  }

  const renderResultByDivision = () => {
    const {
      typeMeasurement,
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      measureEngagement,
      selectedSegment,
      dimensionsData,
    } = props

    if (!validate.exist(idMeasure)) return null
    if (!dimensionsData || !dimensionsData.data) {
      return null
    }

    return (
      <WidgetResultByDivisionEngagement
        typeMeasurement={typeMeasurement}
        idMeasure={idMeasure}
        history={props.history}
        measureTotal={measureTotal}
        measureEngagement={measureEngagement}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
        dimensions={dimensionsData}
      />
    )
  }

  const renderSegmentAnalysis = () => {
    const {
      typeMeasurement,
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      selectedSegment,
    } = props

    if (
      measureGeneral.segments.length > 1 &&
      !verifyFeatureFlag("seg2-5") &&
      (selectedSegment === 0 || selectedSegment === null)
    ) {
      return null
    }

    if (
      measureGeneral.segments.length > 0 &&
      measureGeneral.new_version === 0 &&
      (selectedSegment === 0 || selectedSegment === null)
    ) {
      return null
    }

    let newSelectedSegment = selectedSegment
    if (measureGeneral.segments.length === 1) {
      newSelectedSegment = measureGeneral.segments[0].id_segment
    }

    return (
      <WidgetAnalysisSegment
        typeMeasurement={typeMeasurement}
        idMeasure={idMeasure}
        measureTotal={measureTotal}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={newSelectedSegment}
      />
    )
  }

  const renderQualitativeQuestions = () => {
    const {
      typeMeasurement,
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      selectedSegment,
    } = props

    if (
      (isAmbassadorActive() &&
        !measureGeneral.ambassador_qualitative_results_visible) ||
      !validate.exist(idMeasure)
    ) {
      return null
    }

    return (
      <WidgetQualitativeQuestion
        typeMeasurement={typeMeasurement}
        idMeasure={idMeasure}
        measureTotal={measureTotal}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
      />
    )
  }

  const renderQualitativeBubbles = () => {
    const {
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      selectedSegment,
    } = props

    if (
      (isAmbassadorActive() &&
        !measureGeneral.ambassador_qualitative_results_visible) ||
      !validate.exist(idMeasure)
    ) {
      return null
    }

    return (
      <WidgetQualitativeBubbles
        idMeasure={idMeasure}
        measureTotal={measureTotal}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
      />
    )
  }

  const renderMultipleChoiceQuestions = () => {
    const {
      typeMeasurement,
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureTotal,
      measureGeneral,
      selectedSegment,
    } = props

    if (
      measureGeneral.segments.length > 1 &&
      (selectedSegment === 0 || selectedSegment === null)
    ) {
      return null
    }

    return (
      <WidgetMultipleChoiceQuestions
        typeMeasurement={typeMeasurement}
        idMeasure={idMeasure}
        measureTotal={measureTotal}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
      />
    )
  }

  const renderEvolutionAndResult = () => {
    const {
      measureTotal,
      idDivisionSelected,
      idMeasure,
      measureGeneral,
      idDivisionParent,
      selectedSegment,
      measureEngagement,
    } = props

    let label = "Engagement"
    if (selectedSegment) {
      label = `${label} - ${get(measureGeneral, "segment_name", "")}`
    }

    return (
      <WidgetResume
        measureGeneral={measureGeneral}
        idMeasure={idMeasure}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        className="WidgetResume boxShadow"
        measureData={measureEngagement}
        isLeadership={false}
        selectedSegment={selectedSegment}
        title={
          <Fragment>
            <b>{label}</b>
          </Fragment>
        }
        withEmpty
      />
    )
  }

  const renderSummaryQuestions = () => {
    const {
      idMeasure,
      idDivisionSelected,
      idDivisionParent,
      measureGeneral,
      selectedSegment,
    } = props

    if (
      measureGeneral.segments.length > 1 &&
      (selectedSegment === 0 || selectedSegment === null)
    ) {
      return null
    }

    return (
      <SummaryQuestions
        idMeasure={idMeasure}
        measureGeneral={measureGeneral}
        idDivisionSelected={idDivisionSelected}
        idDivisionParent={idDivisionParent}
        selectedSegment={selectedSegment}
      />
    )
  }

  const renderSummaryWidgets = () => {
    return (<SummaryWidgets
      typeMeasurement={props.typeMeasurement}
      idMeasure={props.idMeasure}
      history={props.history}
      measureGeneral={props.measureGeneral}
      measureTotal={props.measureTotal}
      measureEngagement={props.measureEngagement}
      idDivisionSelected={props.idDivisionSelected}
      idDivisionParent={props.idDivisionParent}
      selectedSegment={props.selectedSegment}
    />)
  }


  // FIX: No hay spinner global. Cada widget tiene su propio spinner.
  // Esto evita desmontar y remontar todos los widgets hijos.
  return (
    <div>
      {renderEvolutionAndResult()}
      {renderSummaryQuestions()}
      {renderSummaryWidgets()}
      {renderResultByDimension()}
      {renderResultByDivision()}
      {renderSegmentAnalysis()}
      {props.measureGeneral.sentiment_analysis_version
        ? renderQualitativeBubbles()
        : renderQualitativeQuestions()}
      {renderMultipleChoiceQuestions()}
    </div>
  )
}

EngagementMeasurement.propTypes = {
  typeMeasurement: PropTypes.string,
  idDivisionParent: PropTypes.number,
  idMeasure: PropTypes.number,
  history: PropTypes.object,
  measureGeneral: PropTypes.object,
  isLoading: PropTypes.bool,
  measureEngagement: PropTypes.object,
  measureTotal: PropTypes.object,
  idDivisionSelected: PropTypes.number,
  getMeasureEngagement: PropTypes.func,
  selectedSegment: PropTypes.number,
}

const mapStateToProps = createStructuredSelector({
  isLoading: makeSelectIsLoading(),
  isLoadingDimensionsData: makeSelectIsLoadingDimensionsData(),
  measureEngagement: makeSelectMeasureEngagement(),
  dimensionsData: makeSelectDimensionsData(),
})

function mapDispatchToProps(dispatch) {
  return {
    getMeasureEngagement: (params) =>
      dispatch(getMeasureEngagementWatcher(params)),
    getDimensionsData: (params) => dispatch(getDimensionsDataWatcher(params)),
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)
const withReducer = injectReducer({ key: "engagementMeasurementV2", reducer })
const withSaga = injectSaga({ key: "engagementMeasurementV2", saga })

export default compose(withReducer, withSaga, withConnect)(EngagementMeasurement)
